<template>
  <v-content class="client users list page">
    <v-progress-linear
      v-if="loading"
      :color="themes.client.light.input"
      indeterminate
      class="my-0"
    />
    <v-container v-else>
      <v-layout column>
        <v-toolbar flat color="transparent" class="mb-4">
          <v-toolbar-title>Users</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            :color="themes.client.light.input"
            prepend-icon="mdi-magnify"
            placeholder="Search"
            class="pb-3"
            hide-details
            single-line
          />
        </v-toolbar>

        <div class="text-xs-right pb-2 px-2">
          <v-btn round icon fab :color="themes.client.light.primaryActionButton" @click="create">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>

        <!-- Clients Index -->

        <div v-for="group in roleGroups" :key="`group-${group}`" class="mb-4">
          <div style="margin-left: -36px">
            <v-chip color="white">
              <v-avatar :size="48"> </v-avatar>
              <span class="title">{{ group }}</span>
            </v-chip>
          </div>
          <div v-for="(client, i) in groupedUsers[group]" :key="`user-${client.id}`">
            <v-card class="my-2" height="72" style="margin-left: 32px">
              <AvatarUpload
                v-if="user.primary || user.id == client.id"
                :src="client.avatar || '/img/missing/avatars/original.png'"
                :path="`/client/clients/${client.id}/avatar`"
                @success="$store.dispatch('client/users/fetch', client.id)"
                :hover-overlay="!!client.avatar"
                overlay
                float-left
              />
              <v-avatar :size="72" style="position: absolute; left: -36px" v-else>
                <v-img :src="client.avatar || '/img/missing/avatars/original.png'" />
              </v-avatar>
              <v-card-title
                :style="
                  `padding: ${
                    user.primary || user.id == client.id ? 0 : 8
                  }px 0 0 40px; margin-top: ${user.primary || user.id == client.id ? -14 : 0}px`
                "
              >
                <div>
                  <div class="title">{{ client.firstNames }} {{ client.lastName }}</div>
                  <div class="subtitle grey--text text--darken-2">
                    <span v-if="client.title">{{ client.title }} - </span>
                    <v-icon small class="text-vertical-middle">mdi-email</v-icon>
                    {{ client.email }}
                  </div>
                  <div class="body-1">
                    {{ client.primary ? 'Owner' : 'Member' }}
                  </div>
                </div>
                <v-spacer v-if="user.primary || user.id == client.id" />
                <v-btn flat icon v-if="user.primary || user.id == client.id" @click="edit(client)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
              </v-card-title>
            </v-card>
          </div>
        </div>

        <!-- Edit Form -->
        <v-dialog v-model="dialog" max-width="600px">
          <v-card>
            <v-card-title>
              <AvatarUpload
                v-if="user.primary || user.id == client.id"
                :src="client.avatar || '/img/missing/avatars/original.png'"
                :path="`/client/clients/${client.id}/avatar`"
                @success="$store.dispatch('client/users/fetch', client.id)"
                :hover-overlay="!!client.avatar"
                overlay
              />
              <v-avatar :size="72" v-else>
                <v-img :src="client.avatar || '/img/missing/avatars/original.png'" />
              </v-avatar>
              <span class="headline pl-3">
                Editing {{ client.firstNames }} {{ client.lastName }}
              </span>
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-form v-model="valid" @submit="submit">
                <v-text-field
                  label="First name"
                  v-model="client.firstNames"
                  :color="themes.client.light.input"
                  :rules="rules.firstNames"
                  required
                />
                <v-text-field
                  label="Surname"
                  v-model="client.lastName"
                  :color="themes.client.light.input"
                  :rules="rules.lastName"
                  required
                />
                <v-text-field
                  label="Title"
                  v-model="client.title"
                  :color="themes.client.light.input"
                />
                <v-text-field
                  label="Email"
                  v-model="client.email"
                  :color="themes.client.light.input"
                  :rules="rules.email"
                  type="email"
                  required
                />
                <v-checkbox
                  label="Receive Weekly Emails"
                  v-model="client.receiveDigests"
                  :color="themes.client.light.input"
                />
                <v-select
                  v-if="user.primary"
                  label="User type"
                  v-model="client.primary"
                  :items="[{ text: 'Owner', value: true }, { text: 'Member', value: false }]"
                  :color="themes.client.light.input"
                />
                <v-autocomplete
                  v-if="user.primary"
                  label="Assigned opportunities"
                  v-model="client.opportunities"
                  :color="themes.client.light.input"
                  :items="opportunities"
                  item-text="name"
                  return-object
                  multiple
                  dense
                  chips
                />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn @click="dialog = false" block flat :color="themes.client.light.cancel">
                Cancel
              </v-btn>
              <v-btn
                @click="submit"
                :color="themes.client.light.primaryActionButton"
                :disabled="!valid"
                :loading="loading"
                block
              >
                {{ editing ? 'Update' : 'Invite' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from '@/mixins/managedDocumentAttributes.js'
// Components
import AvatarUpload from '@/components/client/AvatarUpload'

// Helpers
import { mapGetters } from 'vuex'

// Blank Model
const CLIENT = {
  id: null,
  firstNames: '',
  lastName: '',
  email: '',
  primary: false,
  opportunities: [],
  errors: [],
}

// Vue Component
export default {
  mixins: [managedDocumentAttributes],

  components: { AvatarUpload },
  created() {
    this.$store.dispatch('client/organization/fetch').then(() => {
      this.documentAttributes.title = `${this.organization.name} Users - Leaply`
    })

    this.$store.dispatch('client/users/fetchAll').finally(() => (this.loading = false))
    this.$store.dispatch('client/opportunities/fetchAll')
  },

  data() {
    return {
      search: '',
      loading: true,
      valid: false,
      dialog: false,
      client: Object.assign({}, CLIENT),
      rules: {
        firstNames: [v => !!v || 'must be specified'],
        lastName: [v => !!v || 'must be specified'],
        email: [v => !!v || 'must be specified'],
      },
    }
  },

  computed: {
    editing() {
      return this.client.id != null
    },
    creating() {
      return this.client.id == null
    },
    ...mapGetters({
      users: 'client/users/all',
      user: 'auth/user',
      organization: 'client/organization/model',
      opportunities: 'client/opportunities/all',
    }),
    orderedUsers() {
      if (!this.users || !this.users[0]) return []

      const users = JSON.parse(JSON.stringify(this.users))

      return users
        .filter(u => RegExp(this.search, 'gi').test(`${u.firstNames}${u.lastName}`))
        .sort(({ lastName: a }, { lastName: b }) => {
          if (a > b) return 1
          if (a < b) return -1
          return 0
        })
    },
    groupedUsers() {
      if (!this.users || !this.users[0]) return []

      return _.groupBy(this.orderedUsers, u => (u.primary ? 'Owners' : 'Members'))
    },
    roleGroups() {
      return Object.keys(this.groupedUsers).sort((a, b) => (a < b ? 1 : -1))
    },
  },

  methods: {
    create() {
      this.valid = false
      this.client = Object.assign({}, CLIENT)
      this.dialog = true
    },
    edit(client) {
      this.valid = true
      this.client = Object.assign({}, client)
      this.dialog = true
    },
    submit() {
      if (!this.valid) return

      this.loading = true
      const action = this.editing ? 'client/users/update' : 'client/users/create'
      this.$store
        .dispatch(
          action,
          Object.assign(this.client, {
            organization: { id: this.organization.id },
          })
        )
        .then(
          response => {
            this.dialog = false
            this.$store.dispatch('snackbarSuccess', 'User Saved')
          },
          errors => {
            this.$store.dispatch('snackbarError', 'Failed to Save')
          }
        )
        .finally(() => (this.loading = false))
    },
  },
}
</script>
